import { readonly, type Ref } from 'vue';
import type { AccountConfigHandle } from '../config/accountConfig';
import { useSpellCheckConfig, type SpellCheckConfig } from '../config/spellCheck';
import { resolvedDictionaryFolds } from './dictionaryFolds';

export interface SearchDictionaryFilterSets {
  /** Folds of every never-suggest word (own, company and tenant, any language). */
  hidden: Readonly<Ref<ReadonlySet<string>>>;
  /** Folds of the resolved personal dictionary: no suggestion for a term of it. */
  personal: Readonly<Ref<ReadonlySet<string>>>;
  /** Reads `spell_check` from master again. */
  refresh: () => Promise<void>;
}

/**
 * The caller's never-suggest and personal-dictionary folds (I-3024), read
 * from the resolved `spell_check` of the account config path — the document
 * the settings page edits, so a word hidden or added anywhere leaves or
 * joins every module's suggestions at once and no request of its own is
 * made. Before master's first answer the sets are empty: a suggestion is
 * never held back while the read is out. Without `spell` it reads
 * `useSpellCheckConfig()`.
 */
export function useSearchDictionaryFilters(options?: {
  spell?: AccountConfigHandle<SpellCheckConfig>;
}): SearchDictionaryFilterSets {
  const handle = options?.spell ?? useSpellCheckConfig({ layers: true });
  const folds = resolvedDictionaryFolds(handle);
  return {
    hidden: readonly(folds.never) as Readonly<Ref<ReadonlySet<string>>>,
    personal: readonly(folds.personal) as Readonly<Ref<ReadonlySet<string>>>,
    refresh: () => handle.reload(),
  };
}
